// lib
import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { AbortController as _abortControllerPolyfill } from 'abortcontroller-polyfill/dist/cjs-ponyfill'

import { pluralize } from '@aytm/app/stats/lib/util/helpers'
import { sendJobJSONData } from '@aytm/app/stats/lib/io/fetch'

import { TYPE_CLASS_MAP } from '@aytm/app/stats/page/question-views/components/lib/constants'
import { PUBLISH_ICON, getPublishNotes } from '@aytm/app/stats/page/question-views/components/questions-mapping/lib'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

// components
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'

import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import { default as FooterContainer } from '@aytm/app/stats/page/question-views/components/questions-custom/Footer'

// styles
import style from '@aytm/app/stats/page/question-views/components/questions-mapping/styles/Component.module.scss'

const DEFAULT_FETCH_TIMEOUT = 1000
const FETCH_TIMEOUT_STEP = 500

const AbortController = window.AbortController || _abortControllerPolyfill

const Footer = ({
  model,
  data,
  states,
  prefix,
  command,
  withImages = false,
  updateTextPrefix,
  onChange,
  onUpdateData,
  onFetchApiKey,
  onPublish,
  onPublished,
  onError
}) => {
  const { id, key, group, portal, variables } = model

  const [processing, setProcessing] = useState(false)
  const [published, setPublished] = useState(states.publish.published)

  const abortController = useRef(null)

  const apiKey = useRef(null)

  const portalRef = useRef(null)
  portalRef.current = portal

  const fetchTimeoutRef = useRef(DEFAULT_FETCH_TIMEOUT)
  const fetchDebounceTimeoutRef = useRef(0)
  const fetchTimerRef = useRef(null)

  const error = useMemo(
    () =>
      states.loaded &&
      (variables.some(item => !item.text.trim().length || !item.pattern.trim().length) ||
        (0 < data.controls.portals.options.length && !portal) ||
        states.publish.error),
    [variables, portal, data.controls.portals.options, states.loaded, states.publish.error]
  )

  // No selected portal pushes to the whole set rather than to one named portal.
  const portalsTargeted = null === portal ? data.controls.portals.options.length : 1

  // Composed here rather than per tool; anything the block still supplies wins, so a note a
  // future tool needs can still arrive with it.
  const notes = useMemo(
    () => ({
      ...getPublishNotes({
        mapping: prefix,
        portalsTargeted,
        withImages,
        withEntities: 'survey' !== group
      }),
      ...(data.notes?.publish ?? {})
    }),
    [prefix, portalsTargeted, withImages, group, data.notes?.publish]
  )

  const publishText = useMemo(
    () =>
      `${processing ? 'PUSHING' : published ? 'PUSHED' : 'PUSH'} TO ${pluralize(portalsTargeted, 'PORTAL', 'PORTALS')}`,
    [portalsTargeted, processing, published]
  )
  const publishDisabled = useMemo(
    // Drive the button from the single resolved `error` value — don't re-derive a subset of the publish-error chain
    () => Boolean(error) || !states.loaded || states.disabled.publish || processing || published,
    [error, states.loaded, states.disabled.publish, processing, published]
  )

  const itemTemplate = option => <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
  const valueTemplate = (option, _props) => (
    <span
      dangerouslySetInnerHTML={{ __html: option ? option.label || option.text : _props.placeholder || 'Select...' }}
    ></span>
  )

  const fetchDataDestroy = () => {
    setProcessing(false)

    apiKey.current = null

    fetchTimeoutRef.current = DEFAULT_FETCH_TIMEOUT
    fetchDebounceTimeoutRef.current = 1000

    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current)

    if (null !== abortController.current) {
      abortController.current.abort()
      abortController.current = null
    }

    if (onError && 'function' == typeof onError) {
      onError(false)
    }
  }

  const fetchDataError = () => {
    setProcessing(false)

    apiKey.current = null

    fetchTimeoutRef.current = DEFAULT_FETCH_TIMEOUT
    fetchDebounceTimeoutRef.current = 1000

    if (onPublished && 'function' == typeof onPublished) {
      onPublished(false)
    }
  }

  const fetchData = async (request = {}) => {
    if (null === apiKey.current) {
      fetchDataError()
      return
    }

    request.data.key = apiKey.current.code

    try {
      if (null !== abortController.current) {
        abortController.current.abort()
        abortController.current = null
      }

      abortController.current = new AbortController()

      const { signal } = abortController.current
      const data = await sendJobJSONData({
        ...request,
        signal
      })

      switch (data.status) {
        case 'OK': {
          apiKey.current = null

          setPublished(true)
          setProcessing(false)

          if (onPublished && 'function' == typeof onPublished) {
            const extra = {}
            data.waves && (extra.trackersWaves = data.waves)
            data.portals && (extra.insightsDashboards = data.portals)

            onPublished(true, extra)
          }
          break
        }
        case 'RUNNING': {
          request.data.token = data.token

          fetchTimeoutRef.current += FETCH_TIMEOUT_STEP
          fetchTimerRef.current = setTimeout(() => {
            fetchTimerRef.current = null

            fetchData(request)
          }, fetchTimeoutRef.current)
          break
        }
        case 'INVALID KEY': {
          if (onFetchApiKey && 'function' == typeof onFetchApiKey) {
            onFetchApiKey(key => {
              apiKey.current = JSON.parse(JSON.stringify(key))
              fetchData(request)
            })
          } else fetchDataError()
          break
        }
        default: {
          fetchDataError()
        }
      }

      abortController.current = null
    } catch (error) {
      if (!error.name || (error.name && 'AbortError' !== error.name)) {
        fetchDataError()
      }
    }
  }

  const fetchDataDelay = request => {
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current)

    if (null !== abortController.current) {
      abortController.current.abort()
      abortController.current = null
    }

    const callback = () => {
      fetchTimerRef.current = null

      fetchData(request)
    }

    const callbackTimer = () => {
      if (0 < fetchDebounceTimeoutRef.current) {
        fetchTimerRef.current = setTimeout(callback, fetchDebounceTimeoutRef.current)
      } else callback()
    }

    if (
      null === apiKey.current ||
      (apiKey.current.expire &&
        parseInt(apiKey.current.expire, 10) -
          new Date(PageGlobals.SERVER_TIME == undefined ? Date.now() : PageGlobals.SERVER_TIME).getTime() <
          0)
    ) {
      if (onFetchApiKey && 'function' == typeof onFetchApiKey) {
        onFetchApiKey(key => {
          apiKey.current = JSON.parse(JSON.stringify(key))
          callbackTimer()
        })
      } else setProcessing(false)
    } else callbackTimer()
  }

  const publishMapping = useCallback(() => {
    if (onPublish && 'function' == typeof onPublish) {
      onPublish()
    }

    const request = {
      url: PageGlobals.STAT_JOB_API_URL,
      method: 'POST',
      data: {
        command,
        include: id,
        source: group,
        nocache: 0
      }
    }

    setProcessing(true)
    fetchDataDelay(request)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, group, onPublish])

  useEffect(() => {
    if (
      0 < data.controls.portals.options.length &&
      null !== portalRef.current &&
      !data.controls.portals.options.some(v => v.key == portalRef.current)
    )
      onChange('portal', data.controls.portals.options[0].key)
  }, [data.controls.portals.options, onChange])

  useEffect(() => {
    setPublished(states.publish.published)
  }, [states.publish.published])

  useEffect(() => {
    fetchDataDestroy()
    return () => fetchDataDestroy()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states.error, states.updated])

  useEffect(() => {
    if (processing && (!data.publishState || data.publishState != key)) {
      fetchDataDestroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, group, data.publishState])

  const renderPublish = () => (
    <>
      {0 < data.controls.portals.options.length ? (
        <div className="p-right">
          <Dropdown
            className={`${style.portals} p-dropdown-rounded`}
            placeholder={data.controls.portals.placeholder}
            value={portal}
            options={data.controls.portals.options}
            optionValue="key"
            optionLabel="text"
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
            fixedDropdownDir="bottom"
            onChange={event => onChange('portal', event.value)}
          />
        </div>
      ) : (
        <div className={`${style.info} ${style['info-right']} p-right`}>
          <span>
            Enabled in all {prefix} Portals
            <br />
            under <b>{PageGlobals.ACCOUNT_NAME}</b> account
          </span>
        </div>
      )}
      <div className={`${style.group} p-right p-col`}>
        <Popup
          targetClassName="p-col"
          width="254px"
          trigger={error ? 'hover' : 'none'}
          direction="bottomright"
          show={error ? null : false}
          hideOtherPopupsOnShow
          content={() => (
            <div
              dangerouslySetInnerHTML={{
                __html: error ? notes[states.publish.error] : ''
              }}
            />
          )}
        >
          <Button
            id={`stat-publish-button-custom-question-${id}`}
            className={classNames(
              `stat-publish-button-${TYPE_CLASS_MAP[model.type]}`,
              `-dtr-stat-publish-button-${TYPE_CLASS_MAP[model.type]}`,
              'p-button-grey p-button-rounded',
              { 'p-button-warning': error }
            )}
            data-stat-publish-button={id}
            label={publishText}
            icon={published ? 'pi pi-check' : <i className={PUBLISH_ICON} />}
            loading={processing && !published}
            disabled={publishDisabled}
            onClick={publishDisabled ? null : publishMapping}
          />
        </Popup>
        <Popup
          targetClassName={`${style['footer-help']} p-field`}
          width="400px"
          trigger="click"
          direction="bottomright"
          popupOffset={9}
          hideOtherPopupsOnShow
          content={() => (
            <div
              dangerouslySetInnerHTML={{
                __html: notes.help
              }}
            />
          )}
        >
          <i className="icon-aytmfd-help-v2" />
        </Popup>
      </div>
    </>
  )

  return (
    <FooterContainer model={model} states={states} updateTextPrefix={updateTextPrefix} onUpdateData={onUpdateData}>
      {states.disabled.publish ? null : renderPublish()}
    </FooterContainer>
  )
}

Footer.propTypes = {
  model: PropTypes.object,
  data: PropTypes.object,
  states: PropTypes.object,
  prefix: PropTypes.string,
  command: PropTypes.string,
  updateTextPrefix: PropTypes.string,
  withImages: PropTypes.bool,
  onChange: PropTypes.func,
  onUpdateData: PropTypes.func,
  onFetchApiKey: PropTypes.func,
  onPublish: PropTypes.func,
  onPublished: PropTypes.func,
  onError: PropTypes.func
}

Footer.displayName = 'Footer'

export default Footer
